import { useEffect, useRef, useState, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { MainLayout } from './components/MainLayout';
import { HomeScreen } from './pages/HomeScreen';
import { PantryScreen } from './pages/PantryScreen';
import { MealPlanScreen } from './pages/MealPlanScreen';
import { ShoppingListScreen } from './pages/ShoppingListScreen';
import { FamilyScreen } from './pages/FamilyScreen';
import { MyRecipesScreen } from './pages/MyRecipesScreen';
import { RecipeDetailScreen } from './pages/RecipeDetailScreen';
import { CuisineScreen } from './pages/CuisineScreen';
import { CookScreen } from './pages/CookScreen';
import { CookFromProductScreen } from './pages/CookFromProductScreen';
import { FestiveScreen } from './pages/FestiveScreen';
import { CaloriesScreen } from './pages/CaloriesScreen';
import { TimeScreen } from './pages/TimeScreen';
import { ImportRecipeScreen } from './pages/ImportRecipeScreen';
import { BillingScreen } from './pages/BillingScreen';
import { BillingReturnScreen } from './pages/BillingReturnScreen';
import { LoginPage } from './pages/LoginPage';
import { ConsentScreen } from './components/ConsentScreen';
import { useAuth } from './store/auth';
import { hasTelegramWebApp } from './telegram/detect';
import { bootstrapTelegram } from './telegram/bootstrap';
import { endpoints } from './api/endpoints';
import { track } from './lib/analytics';

// Короткое имя экрана из пути для события screen_view (одно из значений белого списка
// props.screen). Динамические сегменты (/recipe/:id, /cuisine/:key) сворачиваем в общее имя,
// чтобы не плодить кардинальность и не утекали id.
function screenName(pathname: string): string {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/pantry')) return 'pantry';
  if (pathname.startsWith('/from-product')) return 'recipes';
  if (pathname.startsWith('/cook')) return 'cook';
  if (pathname.startsWith('/plan')) return 'menu';
  if (pathname.startsWith('/family')) return 'family';
  if (pathname.startsWith('/my')) return 'recipes';
  if (pathname.startsWith('/recipe')) return 'recipe';
  if (pathname.startsWith('/cuisine')) return 'recipes';
  if (pathname.startsWith('/festive')) return 'recipes';
  if (pathname.startsWith('/calories')) return 'recipes';
  if (pathname.startsWith('/by-time')) return 'recipes';
  if (pathname.startsWith('/billing')) return 'billing';
  if (pathname.startsWith('/import')) return 'import';
  if (pathname.startsWith('/login')) return 'login';
  return 'home';
}

// Централизованный учёт смены экрана: один screen_view на каждый путь. Рендерится внутри
// роутера; ничего не отображает. track() лишь копит событие (флаш отложенный) — на рендер не влияет.
function ScreenTracker() {
  const { pathname } = useLocation();
  // StrictMode в dev дважды вызывает эффекты — без гарда screen_view задвоился бы на каждый путь
  // (app_open защищён модульным флагом, а тут нужен per-path). Шлём только при реальной смене пути.
  const lastPath = useRef<string | null>(null);
  useEffect(() => {
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    track('screen_view', { screen: screenName(pathname) });
  }, [pathname]);
  return null;
}

// app_open фиксируем РОВНО один раз за загрузку приложения — модульный guard переживает
// перемонтирование App (StrictMode в dev) и срабатывает независимо от стартового маршрута.
let appOpenFired = false;

// Статус согласия для возвращающегося юзера спрашиваем у /auth/me ровно один раз за загрузку.
let consentChecked = false;

// Гейт авторизации + согласия: без токена — на /login; авторизован, но согласие не
// собрано (Telegram-юзер/существующий тестер) — блокирующий ConsentScreen.
function Protected({ children }: { children: ReactNode }) {
  const isAuth = useAuth((s) => s.isAuthenticated());
  const consentRequired = useAuth((s) => s.consentRequired);
  if (!isAuth) return <Navigate to="/login" replace />;
  if (consentRequired) return <ConsentScreen />;
  return children;
}

// Дерево маршрутов без роутера — чтобы тестировать в MemoryRouter.
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        element={
          <Protected>
            <MainLayout />
          </Protected>
        }
      >
        <Route path="/" element={<HomeScreen />} />
        <Route path="/pantry" element={<PantryScreen />} />
        <Route path="/plan" element={<MealPlanScreen />} />
        <Route path="/plan/shopping" element={<ShoppingListScreen />} />
        <Route path="/family" element={<FamilyScreen />} />
        <Route path="/my" element={<MyRecipesScreen />} />
      </Route>
      <Route
        path="/recipe/:id"
        element={
          <Protected>
            <RecipeDetailScreen />
          </Protected>
        }
      />
      <Route
        path="/cuisine/:key"
        element={
          <Protected>
            <CuisineScreen />
          </Protected>
        }
      />
      <Route
        path="/cook"
        element={
          <Protected>
            <CookScreen />
          </Protected>
        }
      />
      <Route
        path="/from-product"
        element={
          <Protected>
            <CookFromProductScreen />
          </Protected>
        }
      />
      <Route
        path="/festive"
        element={
          <Protected>
            <FestiveScreen />
          </Protected>
        }
      />
      <Route
        path="/calories"
        element={
          <Protected>
            <CaloriesScreen />
          </Protected>
        }
      />
      <Route
        path="/by-time"
        element={
          <Protected>
            <TimeScreen />
          </Protected>
        }
      />
      <Route
        path="/import"
        element={
          <Protected>
            <ImportRecipeScreen />
          </Protected>
        }
      />
      <Route
        path="/billing"
        element={
          <Protected>
            <BillingScreen />
          </Protected>
        }
      />
      {/* Публичный: возврат с оплаты ЮKassa приходит во внешний браузер без сессии —
          компонент сам показывает «вернитесь в Telegram», а не редиректит на /login. */}
      <Route path="/billing/return" element={<BillingReturnScreen />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// До рендера маршрутов: если мы в Telegram и ещё не вошли — обмениваем init-data на сессию.
// Пока идёт обмен — показываем заглушку (иначе Protected мигнёт экраном входа).
function TelegramBootstrapGate({ children }: { children: ReactNode }) {
  const isAuth = useAuth((s) => s.isAuthenticated());
  // Грузимся (и ждём initData), если присутствует Telegram-SDK — а не только когда initData
  // уже готов: при холодном запуске он приходит с задержкой, иначе юзер падает на /login.
  const [booting, setBooting] = useState(() => hasTelegramWebApp() && !isAuth);
  // StrictMode в dev дважды вызывает эффекты — гард гарантирует один обмен init-data.
  const started = useRef(false);

  useEffect(() => {
    if (!booting || started.current) return;
    started.current = true;
    // setBooting живёт в .finally (вне тела эффекта) — правило set-state-in-effect не срабатывает.
    void bootstrapTelegram()
      .then((ok) => {
        // Self-heal: вошли и есть незакрытая оплата → доактивируем фоном.
        if (ok && localStorage.getItem('recipt-billing-pending')) {
          return endpoints
            .claimPending()
            .then((s) => {
              if (s.active && s.tier !== 'free') localStorage.removeItem('recipt-billing-pending');
            })
            .catch(() => undefined);
        }
        return undefined;
      })
      .finally(() => setBooting(false));
  }, [booting]);

  if (booting) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-screen items-center justify-center bg-cream text-ink/70"
      >
        Загрузка…
      </div>
    );
  }
  return <>{children}</>;
}

export function App() {
  useEffect(() => {
    if (appOpenFired) return;
    appOpenFired = true;
    track('app_open');
    // Возвращающийся юзер (persist-токен): фронт не вызывает /auth/refresh, поэтому
    // статус согласия берём из /auth/me. Новые входы (verify/telegram) ставят флаг сами.
    if (!consentChecked && useAuth.getState().isAuthenticated()) {
      consentChecked = true;
      void endpoints
        .getMe()
        .then((me) => {
          useAuth.getState().setConsentRequired(me.consentRequired);
          useAuth.getState().setSpecialConsent(me.specialConsent);
        })
        .catch(() => undefined);
    }
  }, []);

  return (
    <BrowserRouter>
      <ScreenTracker />
      <TelegramBootstrapGate>
        <AppRoutes />
      </TelegramBootstrapGate>
    </BrowserRouter>
  );
}
